import { useEffect, useRef } from "react";
import { fetchRelationshipSuggestion } from "./useRelationshipSuggestion";
import { shouldPrefillRelationship } from "./relationshipPrefill";

export const runRelationshipPrefill = async ({
  householdId,
  memberId,
  accountId,
  counterpartyMemberId,
  clientToken = null,
  getCurrentValue,
  onPrefill,
}) => {
  if (!householdId || !memberId || typeof onPrefill !== "function") return;
  const { relationship } = await fetchRelationshipSuggestion({
    householdId,
    householdMemberId: memberId,
    accountId,
    counterpartyMemberId,
    clientToken,
  });
  const current = typeof getCurrentValue === "function" ? getCurrentValue() : "";
  if (shouldPrefillRelationship(current, relationship)) onPrefill(relationship);
};

export const useRelationshipPrefill = ({
  enabled = true,
  householdId,
  memberId,
  accountId,
  currentValue,
  clientToken = null,
  onPrefill,
}) => {
  const currentValueRef = useRef(currentValue);
  const onPrefillRef = useRef(onPrefill);
  useEffect(() => {
    currentValueRef.current = currentValue;
  });
  useEffect(() => {
    onPrefillRef.current = onPrefill;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    runRelationshipPrefill({
      householdId,
      memberId,
      accountId,
      clientToken,
      getCurrentValue: () => currentValueRef.current,
      onPrefill: (relationship) => {
        if (!cancelled) onPrefillRef.current(relationship);
      },
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, householdId, memberId, accountId, clientToken]);
};

export const useRelationshipPrefillMany = ({
  enabled = true,
  householdId,
  accountId,
  counterpartyMemberId,
  clientToken = null,
  rows,
  getMemberId,
  getCurrentValue,
  onPrefill,
}) => {
  const memberKey =
    `${counterpartyMemberId || ""}::` +
    (rows || []).map((r, i) => getMemberId(r, i) || "").join("|");
  const prevKeyRef = useRef("");
  const rowsRef = useRef(rows);
  const getMemberIdRef = useRef(getMemberId);
  const getCurrentValueRef = useRef(getCurrentValue);
  const onPrefillRef = useRef(onPrefill);
  useEffect(() => {
    rowsRef.current = rows;
  });
  useEffect(() => {
    getMemberIdRef.current = getMemberId;
  });
  useEffect(() => {
    getCurrentValueRef.current = getCurrentValue;
  });
  useEffect(() => {
    onPrefillRef.current = onPrefill;
  });
  useEffect(() => {
    if (!enabled || prevKeyRef.current === memberKey || !householdId) return undefined;
    prevKeyRef.current = memberKey;
    let cancelled = false;
    (rowsRef.current || []).forEach((row, idx) => {
      const memberId = getMemberIdRef.current(row, idx);
      if (!memberId) return;
      runRelationshipPrefill({
        householdId,
        memberId,
        accountId,
        counterpartyMemberId,
        clientToken,
        getCurrentValue: () => getCurrentValueRef.current(rowsRef.current[idx], idx),
        onPrefill: (relationship) => {
          if (!cancelled) onPrefillRef.current(idx, relationship);
        },
      });
    });
    return () => {
      cancelled = true;
      prevKeyRef.current = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, memberKey, householdId, accountId, clientToken]);
};
